import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateHomePageCarouselBannerInput,
    ModelHomePageCarouselBannerFilterInput,
    HomePageCarouselBanner,
    UpdateHomePageCarouselBannerInput,
    DeleteHomePageCarouselBannerInput,
    UpdateHomePageInput
} from '@app/core/services/api/API.service';
import { AmplifyApiService } from './amplify-api.service';

type ListGraphqlAuthMode = 'apiKey' | 'userPool';

@Injectable({
    providedIn: 'root'
})
export class HomePageCarouselBannerService {
    private _amplifyApiService = inject(AmplifyApiService);
    private amplifyClient = generateClient();
    private _cachedBanners: Map<string, HomePageCarouselBanner[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<HomePageCarouselBanner[]>
    > = new Map();

    constructor() {}

    private _getCacheKey(
        filter: ModelHomePageCarouselBannerFilterInput
    ): string {
        return JSON.stringify(filter || {});
    }

    async listHomePageCarouselBanners(
        filter: ModelHomePageCarouselBannerFilterInput = null,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePageCarouselBanner[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedBanners.has(cacheKey)) {
            return this._cachedBanners.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchBanners(filter, cacheKey, authMode);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchBanners(
        filter: ModelHomePageCarouselBannerFilterInput,
        cacheKey: string,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePageCarouselBanner[]> {
        try {
            const data = ((await this._amplifyApiService.graphql({
                query: queries.listHomePageCarouselBanners,
                variables: {
                    filter
                },
                ...(authMode ? { authMode } : {})
            })) as any).data.listHomePageCarouselBanners.items;

            if (data) {
                const banners = data as HomePageCarouselBanner[];
                this._cachedBanners.set(cacheKey, banners);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return banners;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedBanners.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getHomePageCarouselBanner(
        id: string
    ): Promise<HomePageCarouselBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getHomePageCarouselBanner,
                    variables: {
                        id
                    }
                })
            ).data.getHomePageCarouselBanner;
            if (data) return data as HomePageCarouselBanner;
        } catch (error) {
            console.error('Error finding HomePageCarouselBanner:', error);
            return null;
        }
    }

    async homePageByClienta(): Promise<HomePageCarouselBanner[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listHomePageCarouselBanners
                })
            ).data.listHomePageCarouselBanners.items;
            if (data) return data as HomePageCarouselBanner[];
        } catch (error) {
            console.error('Error finding HomePageCarouselBanner:', error);
            return null;
        }
    }

    async createHomePageCarouselBanner(
        input: CreateHomePageCarouselBannerInput
    ): Promise<HomePageCarouselBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.createHomePageCarouselBanner,
                    variables: {
                        input
                    }
                })
            ).data.createHomePageCarouselBanner;
            if (date) return date as HomePageCarouselBanner;
        } catch (error) {
            console.error('Error creating HomePage:', error);
            return null;
        }
    }

    async updateHomePageCarouselBanner(
        input: UpdateHomePageCarouselBannerInput
    ): Promise<HomePageCarouselBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.updateHomePageCarouselBanner,
                    variables: {
                        input
                    }
                })
            ).data.updateHomePageCarouselBanner;
            if (date) return date as HomePageCarouselBanner;
        } catch (error) {
            console.error('Error updating HomePage:', error);
            return null;
        }
    }

    async deleteHomePageCarouselBanner(
        input: DeleteHomePageCarouselBannerInput
    ): Promise<HomePageCarouselBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteHomePageCarouselBanner,
                    variables: {
                        input
                    }
                })
            ).data.deleteHomePageCarouselBanner;
            if (date) return date as HomePageCarouselBanner;
        } catch (error) {
            console.error('Error delete HomePage:', error);
            return null;
        }
    }
}
